import { Button, Card, Intent, Tooltip } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { segmentTrackRestudyBusClicked } from "analytics/analyticTrackEvents";
import { useBusPageConfiguration } from "contexts/BusPageContext/BusPageConfigurationState";
import React, { useState } from "react";
import { RegionType } from "types/regionType";
import NewScopeDialog from "./NewScopeDialog";
import "./RestudyPanel.scss";
import { useAutoStressBus } from "./useAutoStressBus";

const LOCKED_TOOLTIP = "Contact Nira to unlock re-studies.";

/**
 * Sidebar panel housing the Re-study action, alongside Configuration and
 * Related Queue Projects. A short blurb explains what a re-study does so
 * the primary button isn't a mystery to first-time users. Users without
 * the re-study entitlement see the button locked with a contact tooltip.
 */
const RestudyPanel: React.FC = () => {
    const { canRestudy, bus, niraBusId } = useBusPageConfiguration();
    const [isNewScopeDialogOpen, setIsNewScopeDialogOpen] = useState(false);
    const { autoStressBus, isAutoStressing } = useAutoStressBus();
    const isMisoBus = bus.region === RegionType.MISO;

    return (
        <Card className="RestudyPanel">
            <h5 className="bp5-heading RestudyPanel-title">Re-study</h5>
            <p className="bp5-text-muted RestudyPanel-blurb">
                Study this bus under your own assumptions. Results appear as a
                new scope.
            </p>
            {canRestudy ? (
                <>
                    <Button
                        intent={Intent.PRIMARY}
                        icon={IconNames.REFRESH}
                        text="Re-study Bus"
                        fill
                        onClick={() => {
                            segmentTrackRestudyBusClicked(
                                niraBusId,
                                bus.region
                            );
                            setIsNewScopeDialogOpen(true);
                        }}
                    />
                    {isMisoBus && (
                        <Tooltip
                            content="Automatically kick off a study including queue projects deemed to be related to yours, up to a cap."
                            position="bottom"
                        >
                            <Button
                                icon={IconNames.LIGHTNING}
                                text="Auto-stress Bus"
                                fill
                                loading={isAutoStressing}
                                disabled={isAutoStressing}
                                onClick={autoStressBus}
                            />
                        </Tooltip>
                    )}
                    <NewScopeDialog
                        isOpen={isNewScopeDialogOpen}
                        onClose={() => setIsNewScopeDialogOpen(false)}
                    />
                </>
            ) : (
                <>
                    <Tooltip
                        content={LOCKED_TOOLTIP}
                        placement="bottom"
                        className="RestudyPanel-locked-tooltip"
                    >
                        <Button
                            intent={Intent.PRIMARY}
                            icon={IconNames.LOCK}
                            text="Re-study Bus"
                            disabled
                            fill
                            className="RestudyPanel-locked-button"
                        />
                    </Tooltip>
                    {isMisoBus && (
                        <Tooltip
                            content={LOCKED_TOOLTIP}
                            placement="bottom"
                            className="RestudyPanel-locked-tooltip"
                        >
                            <Button
                                icon={IconNames.LOCK}
                                text="Auto-stress Bus"
                                disabled
                                fill
                                className="RestudyPanel-locked-button"
                            />
                        </Tooltip>
                    )}
                </>
            )}
        </Card>
    );
};

export default RestudyPanel;
